// frontend/src/pages/HrDashboardPage.jsx
// VERSION: 4.0.0 - User-friendly HR Dashboard (Employee->Clients last week + Drilldown + Apply buttons)

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiGet, apiGetAll, listify } from "../api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

// -------------------- Helpers --------------------

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const hoursFmt = (v) =>
  `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}h`;

const pctFmt = (v) => `${Number(v || 0).toFixed(1)}%`;

const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function toISODate(d) {
  return new Date(d).toISOString().slice(0, 10);
}

// last full week Monday->Sunday (not including current partial week)
function getLastFullWeekRange() {
  const now = new Date();
  const day = now.getDay(); // 0 Sun .. 6 Sat
  const diffToMondayThisWeek = (day + 6) % 7; // Monday=0
  const mondayThisWeek = new Date(now);
  mondayThisWeek.setDate(now.getDate() - diffToMondayThisWeek);

  const mondayLastWeek = new Date(mondayThisWeek);
  mondayLastWeek.setDate(mondayThisWeek.getDate() - 7);

  const sundayLastWeek = new Date(mondayLastWeek);
  sundayLastWeek.setDate(mondayLastWeek.getDate() + 6);

  return { start: toISODate(mondayLastWeek), end: toISODate(sundayLastWeek) };
}

// range ending today for "last N weeks"
function getWeeksBackRange(weeks) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - weeks * 7);
  return { start: toISODate(start), end: toISODate(end) };
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white p-3 rounded-lg shadow-xl border border-gray-200">
      <p className="font-extrabold text-gray-900 mb-1">{label}</p>
      {payload.map((entry, idx) => (
        <p key={idx} className="text-sm" style={{ color: entry.color }}>
          {entry.name}:{" "}
          <span className="font-extrabold">
            {typeof entry.value === "number"
              ? entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
}

const useDebugEnabled = () => {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("debug") === "1" || localStorage.getItem("hrDebug") === "1";
  }, [search]);
};

// -------------------- UI Primitives --------------------

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-2xl shadow-lg ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ children, className = "" }) {
  return <div className={`px-6 py-4 border-b border-gray-100 ${className}`}>{children}</div>;
}

function CardTitle({ children }) {
  return <h3 className="text-lg font-extrabold text-gray-900">{children}</h3>;
}

function CardContent({ children, className = "" }) {
  return <div className={`px-6 py-5 ${className}`}>{children}</div>;
}

function Button({
  children,
  onClick,
  variant = "primary",
  className = "",
  title,
  disabled = false,
  type = "button",
}) {
  const variants = {
    primary: "bg-[#007E33] text-white hover:bg-[#006628] shadow-md border border-transparent",
    outline: "bg-white text-gray-800 hover:bg-gray-50 border border-gray-300 shadow-sm",
    subtle: "bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-200 shadow-sm",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-extrabold transition-colors ${
        variants[variant]
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
    >
      {children}
    </button>
  );
}

function Select({ value, onChange, children, className = "" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`border-2 border-gray-200 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007E33] focus:border-transparent bg-white ${className}`}
    >
      {children}
    </select>
  );
}

function Pill({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    red: "bg-red-50 text-red-800 border-red-200",
    purple: "bg-purple-50 text-purple-800 border-purple-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold border ${tones[tone]}`}>
      {children}
    </span>
  );
}

// KPI card uses inline gradient to avoid purge problems
function KPI({ title, value, subtitle, icon, gradient, footerPill }) {
  return (
    <Card className="relative overflow-hidden">
      <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-15" style={{ background: gradient }} />
      <CardContent className="relative">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-extrabold text-gray-600">{title}</p>
          <span className="text-2xl">{icon}</span>
        </div>
        <p className="text-3xl font-extrabold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        {footerPill && <div className="mt-3">{footerPill}</div>}
      </CardContent>
    </Card>
  );
}

function StatCard({ title, value, subtitle, icon }) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-gray-600">{title}</p>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">{value}</p>
            {subtitle ? <p className="text-xs text-gray-500 mt-1">{subtitle}</p> : null}
          </div>
          {icon ? <div className="text-2xl">{icon}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 bg-gray-100 p-2 rounded-2xl">
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            onClick={() => onChange(t.value)}
            className={`px-4 py-2 rounded-xl text-sm font-extrabold transition-all ${
              active ? "bg-white text-[#007E33] shadow" : "text-gray-700 hover:text-gray-900"
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// -------------------- Debug (optional) --------------------

function TailwindProbe({ enabled }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const cs = window.getComputedStyle(ref.current);

    console.group("[HR Debug] TailwindProbe");
    console.log("backgroundColor:", cs.backgroundColor);
    console.log("paddingTop:", cs.paddingTop);
    console.log("borderRadius:", cs.borderTopLeftRadius);
    console.log("boxShadow:", cs.boxShadow);
    console.groupEnd();
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={ref}
      className="bg-red-500 text-white p-4 rounded-xl shadow-lg"
      style={{ position: "fixed", bottom: 16, right: 16, zIndex: 99999 }}
    >
      debug=1
    </div>
  );
}

// -------------------- Time Entry parsing (notes, who, client) --------------------

function extractClientIdFromTimeEntry(t) {
  if (t?.client == null) return null;
  if (typeof t.client === "object") return t.client?.id ?? null;
  return t.client;
}

function extractClientNameFromTimeEntry(t) {
  // Check if client is an object with name/title
  if (t?.client && typeof t.client === "object") {
    return t.client?.name || t.client?.title || null;
  }
  // Check various string fields where client name might be stored
  return t?.client_name || t?.clientName || t?.client_display || null;
}

function extractWhoFromTimeEntry(t) {
  return (
    t?.employee_name ||
    t?.user_name ||
    t?.user_full_name ||
    t?.created_by_name ||
    t?.created_by ||
    t?.user ||
    "Unknown"
  );
}

function extractNoteFromTimeEntry(t) {
  return (
    t?.notes ||
    t?.note ||
    t?.description ||
    t?.memo ||
    t?.narrative ||
    t?.details ||
    ""
  );
}

// -------------------- Page --------------------

export default function HrDashboardPage() {
  const navigate = useNavigate();
  const debug = useDebugEnabled();

  // Main dashboard data
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [clientsDash, setClientsDash] = useState([]); // /dashboard/clients/
  const [activity, setActivity] = useState(null);

  // Invoices for YTD (fix nil)
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Pending vs applied filters (user-friendly Apply button)
  const [pendingEmployee, setPendingEmployee] = useState("");
  const [pendingClient, setPendingClient] = useState("");

  const [appliedEmployee, setAppliedEmployee] = useState("");
  const [appliedClient, setAppliedClient] = useState("");

  const [applyingFilters, setApplyingFilters] = useState(false);

  // Tabs
  const [tab, setTab] = useState("overview");

  // Group view
  const [clientDirectory, setClientDirectory] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupStartDate, setGroupStartDate] = useState(() => {
    const r = getLastFullWeekRange();
    return r.start;
  });
  const [groupEndDate, setGroupEndDate] = useState(() => {
    const r = getLastFullWeekRange();
    return r.end;
  });
  const [groupTimeEntries, setGroupTimeEntries] = useState([]);
  const [groupSubmitting, setGroupSubmitting] = useState(false);

  // Employee drilldown / “clear as day” view
  const lastWeekRange = useMemo(() => getLastFullWeekRange(), []);
  const [lastWeekEntries, setLastWeekEntries] = useState([]);
  const [lastWeekSubmitting, setLastWeekSubmitting] = useState(false);

  const [drillEmployee, setDrillEmployee] = useState("");
  const [drillWeeks, setDrillWeeks] = useState("2");
  const [drillEntries, setDrillEntries] = useState([]);
  const [drillSubmitting, setDrillSubmitting] = useState(false);

  const [expandedNoteId, setExpandedNoteId] = useState(null);

  // --- group field getter (no UI hint text)
  const getClientGroupName = useCallback((c) => {
    const val =
      c?.group_name ||
      c?.client_group ||
      c?.category ||
      c?.segment ||
      "Ungrouped";
    const s = (val ?? "Ungrouped").toString().trim();
    return s.length ? s : "Ungrouped";
  }, []);

  // -------------------- Loading core dashboard --------------------

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");

      const [ov, emps, cls, act] = await Promise.all([
        apiGet("/dashboard/overview/"),
        apiGet("/dashboard/employees/"),
        apiGet("/dashboard/clients/"),
        apiGet("/dashboard/activity/"),
      ]);

      setOverview(ov || {});
      setEmployees(Array.isArray(emps?.employees) ? emps.employees : []);
      setClientsDash(Array.isArray(cls?.clients) ? cls.clients : []);
      setActivity(act || {});
    } catch (e) {
      console.error(e);
      setErr(
        typeof e === "string" ? e : e?.message || "Failed to load dashboard"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Load full client directory for group mapping
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGetAll("/api/clients/");
        setClientDirectory(listify(res));
      } catch (e) {
        try {
          const res2 = await apiGet("/api/clients/");
          setClientDirectory(listify(res2));
        } catch (e2) {
          console.error("Failed to load client directory:", e2);
          setClientDirectory([]);
        }
      }
    })();
  }, []);

  // Load invoices to compute YTD (fix nil issue)
  useEffect(() => {
    (async () => {
      try {
        setInvoicesLoading(true);
        const invData = await apiGet("/api/invoices/");
        setInvoices(listify(invData));
      } catch (e) {
        console.error("Failed to load invoices for YTD:", e);
        setInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    })();
  }, []);

  // Fetch last full week time entries (for “clear as day” employee->client view)
  const loadLastWeekEntries = useCallback(async () => {
    try {
      setLastWeekSubmitting(true);
      const qs = new URLSearchParams();
      qs.append("start_date", lastWeekRange.start);
      qs.append("end_date", lastWeekRange.end);
      const res = await apiGetAll(`/api/time-entries/?${qs.toString()}`);
      const entries = listify(res);
      
      // Don't enrich here - let the useMemo do it after clientIdToName is ready
      setLastWeekEntries(entries);
    } catch (e) {
      console.error("Failed to load last week entries:", e);
      setLastWeekEntries([]);
    } finally {
      setLastWeekSubmitting(false);
    }
  }, [lastWeekRange.start, lastWeekRange.end]);

  useEffect(() => {
    loadLastWeekEntries();
  }, [loadLastWeekEntries]);

  // Apply filters (user-friendly: doesn’t instantly change on select)
  const applyFilters = useCallback(() => {
    setApplyingFilters(true);
    // Small UX delay so users see the "Applying..." state even though this is client-side
    window.setTimeout(() => {
      setAppliedEmployee(pendingEmployee);
      setAppliedClient(pendingClient);
      setApplyingFilters(false);
    }, 250);
  }, [pendingEmployee, pendingClient]);

  const clearFilters = useCallback(() => {
    setPendingEmployee("");
    setPendingClient("");
    setApplyingFilters(true);
    window.setTimeout(() => {
      setAppliedEmployee("");
      setAppliedClient("");
      setApplyingFilters(false);
    }, 250);
  }, []);

  // -------------------- Filtered datasets --------------------

  const employeesFiltered = useMemo(() => {
    if (!appliedEmployee) return employees;
    return employees.filter((e) => String(e.id) === String(appliedEmployee));
  }, [employees, appliedEmployee]);

  const clientsDashFiltered = useMemo(() => {
    if (!appliedClient) return clientsDash;
    return clientsDash.filter((c) => String(c.id) === String(appliedClient));
  }, [clientsDash, appliedClient]);

  // -------------------- KPIs --------------------

  const totals = useMemo(() => {
    const weekHours = employeesFiltered.reduce(
      (sum, e) => sum + safeNum(e?.this_week?.hours),
      0
    );
    const lastWeekHours = employeesFiltered.reduce(
      (sum, e) => sum + safeNum(e?.last_week?.hours),
      0
    );
    const weekBillableAmount = employeesFiltered.reduce(
      (sum, e) => sum + safeNum(e?.this_week?.amount),
      0
    );

    const capacityHours = employeesFiltered.length * 40;
    const utilization =
      capacityHours > 0 ? (weekHours / capacityHours) * 100 : 0;
    const avgHoursPerEmp =
      employeesFiltered.length > 0 ? weekHours / employeesFiltered.length : 0;

    const deltaPct =
      lastWeekHours > 0
        ? ((weekHours - lastWeekHours) / lastWeekHours) * 100
        : 0;
    const overtime = Math.max(0, weekHours - capacityHours);

    return {
      weekHours,
      lastWeekHours,
      weekBillableAmount,
      capacityHours,
      utilization,
      avgHoursPerEmp,
      deltaPct,
      overtime,
    };
  }, [employeesFiltered]);

  const clientIdToName = useMemo(() => {
    const map = new Map();

    // best source: full directory
    for (const c of clientDirectory || []) {
      map.set(String(c.id), c.name || `Client ${c.id}`);
    }

    // fallback: dashboard clients
    for (const c of clientsDash || []) {
      if (!map.has(String(c.id))) {
        map.set(String(c.id), c.name || `Client ${c.id}`);
      }
    }

    return map;
  }, [clientDirectory, clientsDash]);

  const clientRevenueData = useMemo(() => {
    return clientsDashFiltered.map((c) => {
      const hours = safeNum(c.time_hours);
      const revenue = safeNum(c.time_amount);
      return {
        id: c.id,
        name: c.name || `#${c.id}`,
        hours,
        revenue,
        wip: safeNum(c.wip),
        ar: safeNum(c.ar_balance),
        avgRate: hours > 0 ? revenue / hours : 0,
      };
    });
  }, [clientsDashFiltered]);

  const revenueMetrics = useMemo(() => {
    const totalRevenue = clientRevenueData.reduce(
      (sum, c) => sum + c.revenue,
      0
    );
    const totalHours = clientRevenueData.reduce((sum, c) => sum + c.hours, 0);
    const avgRate = totalHours > 0 ? totalRevenue / totalHours : 0;
    return { totalRevenue, totalHours, avgRate };
  }, [clientRevenueData]);

  // Invoices YTD computed from /api/invoices/
  const invoicesYtd = useMemo(() => {
    const year = new Date().getFullYear();
    const inYear = invoices.filter((i) => {
      const d = (i.date || "").slice(0, 10);
      return d && Number(d.slice(0, 4)) === year;
    });
    const total = inYear.reduce((sum, inv) => sum + safeNum(inv.total), 0);
    return { year, count: inYear.length, total };
  }, [invoices]);

  // Trend chart uses overview if present
  const timeTrendData = useMemo(() => {
    const thisWeek = safeNum(overview?.time?.this_week?.hours);
    const lastWeek = safeNum(overview?.time?.last_week?.hours);
    const thisMonth = safeNum(overview?.time?.this_month?.hours);
    const lastMonth = safeNum(overview?.time?.last_month?.hours);
    return [
      { period: "Last Month", hours: lastMonth },
      { period: "This Month", hours: thisMonth },
      { period: "Last Week", hours: lastWeek },
      { period: "This Week", hours: thisWeek },
    ];
  }, [overview]);

  // -------------------- “Clear as day” employee -> clients (last week) --------------------

  const employeeLastWeekSummary = useMemo(() => {
    console.log('=== Building Employee Last Week Summary ===');
    console.log('lastWeekEntries count:', lastWeekEntries.length);
    console.log('clientIdToName size:', clientIdToName.size);
    console.log('clientIdToName sample:', Array.from(clientIdToName.entries()).slice(0, 5));
    
    // Build a per-employee -> per-client hours map for last week
    const byEmployee = new Map();

    for (const t of lastWeekEntries) {
      const who = extractWhoFromTimeEntry(t);
      const hours = safeNum(t.hours);
      const cid = extractClientIdFromTimeEntry(t);
      
      console.log('Processing entry:', {
        who,
        clientId: cid,
        rawClientField: t.client,
        clientName: t.client_name,
      });
      
      if (cid == null) continue; // skip entries without a client

      const clientName =
        extractClientNameFromTimeEntry(t) ||
        clientIdToName.get(String(cid)) ||
        `Client ${cid}`;
      
      console.log('Resolved client name:', clientName, 'for ID:', cid);

      const emp = byEmployee.get(who) || {
        employee: who,
        totalHours: 0,
        clients: new Map(),
      };
      emp.totalHours += hours;

      emp.clients.set(clientName, (emp.clients.get(clientName) || 0) + hours);
      byEmployee.set(who, emp);
    }

    const out = Array.from(byEmployee.values()).map((e) => ({
      employee: e.employee,
      totalHours: e.totalHours,
      clients: Array.from(e.clients.entries())
        .map(([name, hrs]) => ({ name, hours: hrs }))
        .sort((a, b) => b.hours - a.hours),
    }));

    // Sort by total hours descending
    out.sort((a, b) => b.totalHours - a.totalHours);
    console.log('=== Final Summary ===', out);
    return out;
  }, [lastWeekEntries, clientIdToName]);

  // -------------------- Employee drilldown (Submit button + notes) --------------------

  const submitEmployeeDrilldown = useCallback(async () => {
    if (!drillEmployee) {
      setDrillEntries([]);
      return;
    }

    try {
      setDrillSubmitting(true);
      const weeks = Math.max(1, Number(drillWeeks || 2));
      const { start, end } = getWeeksBackRange(weeks);

      const qs = new URLSearchParams();
      qs.append("start_date", start);
      qs.append("end_date", end);

      const res = await apiGetAll(`/api/time-entries/?${qs.toString()}`);
      const all = listify(res);

      // Filter by employee name (best we can do frontend-only without a server param)
      const targetName =
        employees.find((e) => String(e.id) === String(drillEmployee))?.name ||
        drillEmployee;
      const filtered = all.filter((t) => {
        const who = String(extractWhoFromTimeEntry(t) || "").toLowerCase();
        return who.includes(String(targetName).toLowerCase());
      });

      // Sort newest first if date exists
      filtered.sort((a, b) =>
        String(b.date || "").localeCompare(String(a.date || ""))
      );

      setDrillEntries(filtered);
      setExpandedNoteId(null);
    } catch (e) {
      console.error("Failed employee drilldown:", e);
      setDrillEntries([]);
    } finally {
      setDrillSubmitting(false);
    }
  }, [drillEmployee, drillWeeks, employees]);

  // -------------------- Group view (Submit button) --------------------

  const clientIdToGroup = useMemo(() => {
    const map = new Map();
    for (const c of clientDirectory)
      map.set(String(c.id), getClientGroupName(c));
    return map;
  }, [clientDirectory, getClientGroupName]);

  const allGroups = useMemo(() => {
    const set = new Set();
    for (const c of clientDirectory) set.add(getClientGroupName(c));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [clientDirectory, getClientGroupName]);

  const selectedGroupClientIds = useMemo(() => {
    if (!selectedGroup) return new Set();
    const ids = new Set();
    for (const c of clientDirectory) {
      if (getClientGroupName(c) === selectedGroup) ids.add(String(c.id));
    }
    return ids;
  }, [clientDirectory, selectedGroup, getClientGroupName]);

  const groupRollupFromDashboardClients = useMemo(() => {
    const byGroup = new Map();
    for (const c of clientsDash) {
      const g = clientIdToGroup.get(String(c.id)) || "Ungrouped";
      const prev = byGroup.get(g) || {
        group: g,
        clientCount: 0,
        wip: 0,
        ar: 0,
        timeHours: 0,
        timeAmount: 0,
      };

      prev.clientCount += 1;
      prev.wip += safeNum(c.wip);
      prev.ar += safeNum(c.ar_balance);
      prev.timeHours += safeNum(c.time_hours);
      prev.timeAmount += safeNum(c.time_amount);
      byGroup.set(g, prev);
    }
    return Array.from(byGroup.values()).sort(
      (a, b) => b.timeAmount - a.timeAmount
    );
  }, [clientsDash, clientIdToGroup]);

  const selectedGroupRow = useMemo(() => {
    if (!selectedGroup) return null;
    return (
      groupRollupFromDashboardClients.find(
        (r) => r.group === selectedGroup
      ) || {
        group: selectedGroup,
        clientCount: 0,
        wip: 0,
        ar: 0,
        timeHours: 0,
        timeAmount: 0,
      }
    );
  }, [selectedGroup, groupRollupFromDashboardClients]);

  const submitGroupView = useCallback(async () => {
    if (!selectedGroup) {
      setGroupTimeEntries([]);
      return;
    }
    try {
      setGroupSubmitting(true);
      const qs = new URLSearchParams();
      if (groupStartDate) qs.append("start_date", groupStartDate);
      if (groupEndDate) qs.append("end_date", groupEndDate);

      const res = await apiGetAll(`/api/time-entries/?${qs.toString()}`);
      setGroupTimeEntries(listify(res));
    } catch (e) {
      console.error("Failed to load group time entries:", e);
      setGroupTimeEntries([]);
    } finally {
      setGroupSubmitting(false);
    }
  }, [selectedGroup, groupStartDate, groupEndDate]);

  const selectedGroupTime = useMemo(() => {
    if (!selectedGroup)
      return { totalHours: 0, totalBillable: 0, byClient: [], byPerson: [] };

    const hoursByClient = new Map();
    const billableByClient = new Map();
    const hoursByPerson = new Map();

    for (const t of groupTimeEntries) {
      const cidRaw = extractClientIdFromTimeEntry(t);
      if (cidRaw == null) continue;
      const cid = String(cidRaw);
      if (!selectedGroupClientIds.has(cid)) continue;

      const hrs = safeNum(t.hours);
      const bill = safeNum(t.billable_amount || t.amount || 0);

      const cName =
        extractClientNameFromTimeEntry(t) ||
        clientDirectory.find((c) => String(c.id) === cid)?.name ||
        clientsDash.find((c) => String(c.id) === cid)?.name ||
        `Client ${cid}`;

      hoursByClient.set(cName, (hoursByClient.get(cName) || 0) + hrs);
      billableByClient.set(cName, (billableByClient.get(cName) || 0) + bill);

      const who = extractWhoFromTimeEntry(t);
      hoursByPerson.set(who, (hoursByPerson.get(who) || 0) + hrs);
    }

    const totalHours = Array.from(hoursByClient.values()).reduce(
      (a, b) => a + b,
      0
    );
    const totalBillable = Array.from(billableByClient.values()).reduce(
      (a, b) => a + b,
      0
    );

    const byClient = Array.from(hoursByClient.entries())
      .map(([name, hrs]) => ({
        name,
        hours: hrs,
        pct: totalHours > 0 ? Number(((hrs / totalHours) * 100).toFixed(1)) : 0,
        billable: Number((billableByClient.get(name) || 0).toFixed(2)),
      }))
      .sort((a, b) => b.hours - a.hours);

    const byPerson = Array.from(hoursByPerson.entries())
      .map(([name, hrs]) => ({ name, hours: hrs }))
      .sort((a, b) => b.hours - a.hours);

    return { totalHours, totalBillable, byClient, byPerson };
  }, [
    selectedGroup,
    groupTimeEntries,
    selectedGroupClientIds,
    clientDirectory,
    clientsDash,
  ]);

  // Charts data
  const topClients = useMemo(() => {
    return [...clientRevenueData]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
  }, [clientRevenueData]);

  const clientDistribution = useMemo(() => {
    const sorted = [...clientRevenueData].sort((a, b) => b.hours - a.hours);
    const top5 = sorted.slice(0, 5);
    const others = sorted.slice(5);
    const othersTotal = others.reduce((sum, c) => sum + c.hours, 0);
    const data = top5.map((c) => ({ name: c.name, value: c.hours }));
    if (othersTotal > 0) data.push({ name: "Others", value: othersTotal });
    return data;
  }, [clientRevenueData]);

  // Debug
  useEffect(() => {
    if (!debug) return;
    console.group("[HR Debug]");
    console.log({
      employees: employees.length,
      clientsDash: clientsDash.length,
      clientDirectory: clientDirectory.length,
      lastWeekRange,
      lastWeekEntries: lastWeekEntries.length,
      invoices: invoices.length,
      invoicesYtd,
    });
    console.groupEnd();
  }, [
    debug,
    employees.length,
    clientsDash.length,
    clientDirectory.length,
    lastWeekRange,
    lastWeekEntries.length,
    invoices.length,
    invoicesYtd,
  ]);

  // -------------------- Loading / error --------------------

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#007E33] mx-auto mb-3" />
          <p className="text-sm font-extrabold text-gray-700">
            Loading dashboard…
          </p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-xl w-full">
          <CardContent className="py-10 text-center">
            <p className="text-red-600 font-extrabold mb-4">{err}</p>
            <Button onClick={fetchDashboard}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const weekTrendTone =
    totals.deltaPct > 0 ? "green" : totals.deltaPct < 0 ? "red" : "gray";
  const COLORS = [
    "#007E33",
    "#34A853",
    "#4CAF50",
    "#66BB6A",
    "#81C784",
    "#A5D6A7",
    "#C8E6C9",
  ];

  return (
    <div className="w-full">
      <TailwindProbe enabled={debug} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900">
          HR Manager Dashboard
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Last week work visibility + drilldowns + utilization, revenue and
          collections.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="subtle"
            onClick={fetchDashboard}
            title="Refresh dashboard"
          >
            🔄 Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/time")}
            title="Time entry"
          >
            ⏱️ Time Entry
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/invoices")}
            title="Invoices"
          >
            🧾 Invoices
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/clients")}
            title="Clients"
          >
            🏢 Clients
          </Button>
        </div>
      </div>

      {/* Filters (pending + apply) */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-extrabold text-gray-700 mb-2">
                Employee
              </label>
              <Select value={pendingEmployee} onChange={setPendingEmployee}>
                <option value="">All Employees</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-700 mb-2">
                Client
              </label>
              <Select value={pendingClient} onChange={setPendingClient}>
                <option value="">All Clients</option>
                {clientsDash.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button
                className="w-full"
                variant="primary"
                onClick={applyFilters}
                disabled={applyingFilters}
              >
                {applyingFilters ? "Applying..." : "Apply Filters"}
              </Button>
            </div>

            <div className="flex items-end gap-2">
              <Button
                className="w-full"
                variant="outline"
                onClick={clearFilters}
                disabled={applyingFilters}
              >
                Clear
              </Button>
            </div>
          </div>

          {(appliedEmployee || appliedClient) && (
            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              <Pill tone="blue">
                Active Filters:
                {appliedEmployee ? " Employee" : ""}
                {appliedClient ? " Client" : ""}
              </Pill>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="mb-4">
        <h2 className="text-lg font-extrabold text-gray-900">Key Metrics</h2>
        <p className="text-sm text-gray-600 mt-1">
          Weekly workload + capacity, billables, and collections.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPI
          title="Hours This Week"
          value={hoursFmt(totals.weekHours)}
          subtitle={`Avg ${hoursFmt(totals.avgHoursPerEmp)} / employee`}
          icon="⏱️"
          gradient="linear-gradient(135deg, #10B981, #14B8A6)"
          footerPill={
            <Pill tone={weekTrendTone}>
              {totals.deltaPct >= 0 ? "↗" : "↘"}{" "}
              {Math.abs(totals.deltaPct).toFixed(1)}% vs last week
            </Pill>
          }
        />
        <KPI
          title="Utilization"
          value={pctFmt(totals.utilization)}
          subtitle={`${employeesFiltered.length} employees • ${hoursFmt(
            totals.capacityHours
          )} capacity`}
          icon="🎯"
          gradient="linear-gradient(135deg, #3B82F6, #06B6D4)"
          footerPill={
            totals.overtime > 0 ? (
              <Pill tone="orange">⚠️ Overtime {hoursFmt(totals.overtime)}</Pill>
            ) : (
              <Pill tone="green">On capacity</Pill>
            )
          }
        />
        <KPI
          title="Billable (This Week)"
          value={money(totals.weekBillableAmount)}
          subtitle="From employee billables"
          icon="💵"
          gradient="linear-gradient(135deg, #6366F1, #2563EB)"
        />
        <KPI
          title="Avg Billable Rate"
          value={money(revenueMetrics.avgRate)}
          subtitle={`${hoursFmt(revenueMetrics.totalHours)} billable hours`}
          icon="📈"
          gradient="linear-gradient(135deg, #8B5CF6, #EC4899)"
        />
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "overview", label: "📌 Last Week" },
            { value: "employeeDrill", label: "🔎 Employee Drilldown" },
            { value: "clients", label: "🏢 Clients" },
            { value: "groups", label: "🗂️ Groups" },
            { value: "finance", label: "💰 Finance" },
          ]}
        />
      </div>

      {/* ==================== OVERVIEW (Last Week) ==================== */}
      {tab === "overview" && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex items-center justify-between gap-4">
              <CardTitle>
                Last Week: What Clients Each Employee Worked On
              </CardTitle>

              <Button
                variant="primary"
                onClick={loadLastWeekEntries}
                disabled={lastWeekSubmitting}
                title="Reload last week time entries"
              >
                {lastWeekSubmitting ? "Submitting..." : "Refresh Last Week"}
              </Button>
            </CardHeader>

            <CardContent>
              <div className="text-sm text-gray-600 mb-4">
                Period:{" "}
                <span className="font-extrabold text-gray-900">
                  {lastWeekRange.start}
                </span>{" "}
                →{" "}
                <span className="font-extrabold text-gray-900">
                  {lastWeekRange.end}
                </span>
              </div>

              {lastWeekSubmitting ? (
                <div className="py-6 text-center text-gray-600">
                  Loading last week…
                </div>
              ) : employeeLastWeekSummary.length === 0 ? (
                <div className="py-6 text-center text-gray-600">
                  No time entries found for last week.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {employeeLastWeekSummary.map((e) => (
                    <div
                      key={e.employee}
                      className="p-4 rounded-2xl border border-gray-200 bg-gray-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-base font-extrabold text-gray-900 truncate">
                            {e.employee}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Total:{" "}
                            <span className="font-extrabold">
                              {hoursFmt(e.totalHours)}
                            </span>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          onClick={() => {
                            // Try to preselect employee in drilldown
                            const match = employees.find(
                              (x) =>
                                (x.name || "").toLowerCase() ===
                                String(e.employee).toLowerCase()
                            );
                            setDrillEmployee(match ? String(match.id) : "");
                            setTab("employeeDrill");
                          }}
                        >
                          Drill down →
                        </Button>
                      </div>

                      <div className="mt-3 space-y-2">
                        {e.clients.slice(0, 8).map((c) => (
                          <div
                            key={c.name}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="text-gray-800 font-semibold truncate pr-3">
                              {c.name}
                            </div>
                            <div className="text-gray-700">
                              {hoursFmt(c.hours)}
                            </div>
                          </div>
                        ))}
                        {e.clients.length > 8 && (
                          <div className="text-xs text-gray-500 mt-1">
                            +{e.clients.length - 8} more clients…
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Hours Trend (from Overview)</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="period" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="hours"
                      name="Hours"
                      stroke="#007E33"
                      strokeWidth={3}
                      dot={{ fill: "#007E33", r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Invoices YTD</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <StatCard
                  title={`Total (${invoicesYtd.year})`}
                  value={
                    invoicesLoading ? "Submitting..." : money(invoicesYtd.total)
                  }
                  subtitle={
                    invoicesLoading ? "" : `${invoicesYtd.count} invoices`
                  }
                  icon="🧾"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ==================== EMPLOYEE DRILLDOWN ==================== */}
      {tab === "employeeDrill" && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex items-center justify-between gap-4">
              <CardTitle>Employee Drilldown</CardTitle>

              <Button
                variant="primary"
                onClick={submitEmployeeDrilldown}
                disabled={drillSubmitting || !drillEmployee}
                title="Load employee activity"
              >
                {drillSubmitting ? "Submitting..." : "Load"}
              </Button>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-extrabold text-gray-700 mb-2">
                    Employee
                  </label>
                  <Select value={drillEmployee} onChange={setDrillEmployee}>
                    <option value="">Select employee…</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-extrabold text-gray-700 mb-2">
                    Range
                  </label>
                  <Select value={drillWeeks} onChange={setDrillWeeks}>
                    <option value="2">Last 2 weeks</option>
                    <option value="3">Last 3 weeks</option>
                    <option value="4">Last 4 weeks</option>
                    <option value="6">Last 6 weeks</option>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      setDrillEntries([]);
                      setExpandedNoteId(null);
                    }}
                  >
                    Clear Results
                  </Button>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                Tip: Notes are shown per entry — click “Show note” to expand.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Entries (with notes)</CardTitle>
            </CardHeader>
            <CardContent>
              {drillSubmitting ? (
                <div className="py-8 text-center text-gray-600">
                  Submitting…
                </div>
              ) : drillEntries.length === 0 ? (
                <div className="py-8 text-center text-gray-600">
                  Select an employee and click{" "}
                  <span className="font-extrabold">Load</span>.
                </div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4">Client</th>
                        <th className="py-2 pr-4">Code</th>
                        <th className="py-2 pr-4 text-right">Hours</th>
                        <th className="py-2 pr-4">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillEntries.slice(0, 250).map((t) => {
                        const id =
                          t.id ??
                          `${t.date}-${t.hours}-${extractClientIdFromTimeEntry(
                            t
                          )}`;
                        const note = extractNoteFromTimeEntry(t);
                        const hasNote = Boolean(
                          note && String(note).trim().length
                        );
                        const expanded = expandedNoteId === id;
                        
                        const cid = extractClientIdFromTimeEntry(t);
                        const clientName =
                          extractClientNameFromTimeEntry(t) ||
                          clientIdToName.get(String(cid)) ||
                          `Client ${cid ?? "-"}`;

                        return (
                          <tr key={id} className="border-b align-top">
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {(t.date || "").slice(0, 10) || "-"}
                            </td>
                            <td className="py-2 pr-4 font-semibold text-gray-800">
                              {clientName}
                            </td>
                            <td className="py-2 pr-4">
                              {t.time_code || t.code || "-"}
                            </td>
                            <td className="py-2 pr-4 text-right">
                              {hoursFmt(t.hours)}
                            </td>
                            <td className="py-2 pr-4">
                              {!hasNote ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <div>
                                  <button
                                    className="text-[#007E33] underline font-semibold"
                                    onClick={() =>
                                      setExpandedNoteId(expanded ? null : id)
                                    }
                                  >
                                    {expanded ? "Hide note" : "Show note"}
                                  </button>
                                  {expanded && (
                                    <div className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50 whitespace-pre-wrap">
                                      {note}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {drillEntries.length > 250 && (
                    <div className="text-xs text-gray-500 mt-3">
                      Showing first 250 entries. Narrow the range to view fewer.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== CLIENTS ==================== */}
      {tab === "clients" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Clients by Revenue</CardTitle>
            </CardHeader>
            <CardContent className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topClients}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" stroke="#6B7280" tick={false} />
                  <YAxis stroke="#6B7280" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="#007E33"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Client Time Distribution</CardTitle>
            </CardHeader>
            <CardContent className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    labelLine={false}
                  >
                    {clientDistribution.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== GROUPS ==================== */}
      {tab === "groups" && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex items-center justify-between gap-4">
              <CardTitle>Group View</CardTitle>

              <Button
                variant="primary"
                onClick={submitGroupView}
                disabled={groupSubmitting || !selectedGroup}
              >
                {groupSubmitting ? "Submitting..." : "Load"}
              </Button>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-extrabold text-gray-700 mb-2">
                    Group
                  </label>
                  <Select value={selectedGroup} onChange={setSelectedGroup}>
                    <option value="">Select a group…</option>
                    {allGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-extrabold text-gray-700 mb-2">
                    Start
                  </label>
                  <input
                    type="date"
                    value={groupStartDate}
                    onChange={(e) => setGroupStartDate(e.target.value)}
                    className="border-2 border-gray-200 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007E33] focus:border-transparent bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-extrabold text-gray-700 mb-2">
                    End
                  </label>
                  <input
                    type="date"
                    value={groupEndDate}
                    onChange={(e) => setGroupEndDate(e.target.value)}
                    className="border-2 border-gray-200 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#007E33] focus:border-transparent bg-white"
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      setSelectedGroup("");
                      setGroupTimeEntries([]);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedGroup ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <StatCard
                title="Group Hours"
                value={hoursFmt(selectedGroupTime.totalHours)}
                subtitle={`${groupStartDate} → ${groupEndDate}`}
                icon="⏱️"
              />
              <StatCard
                title="Group Billables"
                value={money(selectedGroupTime.totalBillable)}
                subtitle="From time entries"
                icon="💵"
              />
              <StatCard
                title="Group WIP"
                value={money(selectedGroupRow?.wip || 0)}
                subtitle="Sum of client WIP"
                icon="📌"
              />
              <StatCard
                title="Group A/R"
                value={money(selectedGroupRow?.ar || 0)}
                subtitle="Sum of client receivables"
                icon="💰"
              />
            </div>
          ) : (
            <Card>
              <CardContent className="text-gray-600">
                Select a group and click Load.
              </CardContent>
            </Card>
          )}

          {selectedGroup ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Allocation by Client (% of group hours)</CardTitle>
                </CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={selectedGroupTime.byClient.slice(0, 12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" stroke="#6B7280" tick={false} />
                      <YAxis stroke="#6B7280" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="pct"
                        name="% of Hours"
                        fill="#007E33"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Who Worked on This Group</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {selectedGroupTime.byPerson.slice(0, 15).map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between p-2 rounded-lg border border-gray-200 bg-gray-50"
                    >
                      <div className="text-sm font-extrabold text-gray-800">
                        {p.name}
                      </div>
                      <div className="text-sm text-gray-700">
                        {hoursFmt(p.hours)}
                      </div>
                    </div>
                  ))}
                  {!selectedGroupTime.byPerson.length && (
                    <div className="text-sm text-gray-500">
                      No time entries found for this group in that range.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {selectedGroup ? (
            <Card>
              <CardHeader>
                <CardTitle>Group Client Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-4">Client</th>
                        <th className="py-2 pr-4">Hours</th>
                        <th className="py-2 pr-4">% of Group</th>
                        <th className="py-2 pr-4">Billable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGroupTime.byClient.map((r) => (
                        <tr key={r.name} className="border-b last:border-b-0">
                          <td className="py-2 pr-4 font-extrabold text-gray-800">
                            {r.name}
                          </td>
                          <td className="py-2 pr-4">{hoursFmt(r.hours)}</td>
                          <td className="py-2 pr-4">{r.pct}%</td>
                          <td className="py-2 pr-4">{money(r.billable)}</td>
                        </tr>
                      ))}
                      {!selectedGroupTime.byClient.length && (
                        <tr>
                          <td className="py-6 text-gray-500" colSpan={4}>
                            No client time found for this group in the date
                            range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* ==================== FINANCE ==================== */}
      {tab === "finance" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                <p className="text-xs font-extrabold text-gray-600 uppercase">
                  Total Revenue
                </p>
                <p className="text-3xl font-extrabold text-[#007E33] mt-1">
                  {money(revenueMetrics.totalRevenue)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-gray-200 bg-white">
                  <p className="text-xs font-extrabold text-gray-600 uppercase">
                    Billable Hours
                  </p>
                  <p className="text-xl font-extrabold text-gray-900 mt-1">
                    {hoursFmt(revenueMetrics.totalHours)}
                  </p>
                </div>
                <div className="p-4 rounded-xl border border-gray-200 bg-white">
                  <p className="text-xs font-extrabold text-gray-600 uppercase">
                    Avg Rate
                  </p>
                  <p className="text-xl font-extrabold text-gray-900 mt-1">
                    {money(revenueMetrics.avgRate)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                  <p className="text-xs font-extrabold text-gray-600 uppercase">
                    Total WIP
                  </p>
                  <p className="text-xl font-extrabold text-purple-700 mt-1">
                    {money(overview?.wip?.total)}
                  </p>
                </div>
                <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                  <p className="text-xs font-extrabold text-gray-600 uppercase">
                    Total A/R
                  </p>
                  <p className="text-xl font-extrabold text-orange-700 mt-1">
                    {money(overview?.accounts_receivable?.total)}
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-white">
                <p className="text-xs font-extrabold text-gray-600 uppercase">
                  Invoices YTD
                </p>
                <p className="text-xl font-extrabold text-gray-900 mt-1">
                  {invoicesLoading ? "Submitting..." : money(invoicesYtd.total)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {invoicesLoading
                    ? ""
                    : `${invoicesYtd.count} invoices • ${invoicesYtd.year}`}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate("/clients")}
              >
                Go to Clients
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate("/invoices")}
              >
                Go to Invoices
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate("/employees")}
              >
                Go to Employees
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}